/**
 * 招待トークンセキュリティサービス
 * セキュアな招待トークンの生成・検証・管理を提供
 */

import logger from '../utils/logger';
import CryptoJS from 'crypto-js';

// トークン設定
const TOKEN_EXPIRY_HOURS = 72; // 72時間有効
const SECRET_KEY = import.meta.env.VITE_INVITATION_SECRET || 'salonhub-invitation-secret-2024';
const USED_TOKENS_KEY = 'salonhub:used_invitation_tokens';
// const TOKEN_CACHE_KEY = 'salonhub:invitation_token_cache'; // Reserved for future use

/**
 * セキュアな招待トークン構造
 */
export interface SecureInvitationToken {
  token: string;
  signature: string;
  expiresAt: number;
  organizationId: string;
  organizationName?: string;
  invitedEmail: string;
  role: 'owner' | 'admin' | 'manager' | 'staff' | 'viewer';
  inviterUserId?: string;
  metadata?: Record<string, any>;
  usedAt?: number;
}

/**
 * トークン生成データ
 */
export interface InvitationTokenData {
  organizationId: string;
  organizationName?: string;
  invitedEmail: string;
  role: 'owner' | 'admin' | 'manager' | 'staff' | 'viewer';
  inviterUserId?: string;
  metadata?: Record<string, any>;
}

/**
 * トークン検証結果
 */
export interface TokenValidationResult {
  valid: boolean;
  expired?: boolean;
  used?: boolean;
  tampered?: boolean;
  data?: SecureInvitationToken;
  error?: string;
}

/**
 * 招待トークン管理サービス
 */
export class InvitationService {
  // メモリキャッシュ（使用済みトークン）
  private static usedTokensCache: Set<string> = new Set();
  private static cacheInitialized = false;

  /**
   * キャッシュ初期化
   */
  private static initializeCache(): void {
    if (this.cacheInitialized) return;

    try {
      // LocalStorageから使用済みトークンを読み込み
      const storedTokens = localStorage.getItem(USED_TOKENS_KEY);
      if (storedTokens) {
        const tokens = JSON.parse(storedTokens);
        Object.keys(tokens).forEach(token => this.usedTokensCache.add(token));
      }
      this.cacheInitialized = true;
      logger.info('[InvitationService] キャッシュ初期化完了');
    } catch (error) {
      logger.error('[InvitationService] キャッシュ初期化エラー:', error);
    }
  }

  /**
   * セキュアなトークン生成
   */
  static generateToken(data: InvitationTokenData): string {
    try {
      const now = Date.now();
      const expiresAt = now + (TOKEN_EXPIRY_HOURS * 60 * 60 * 1000);

      // トークンペイロード
      const tokenData: SecureInvitationToken = {
        token: this.generateRandomToken(),
        signature: '',
        expiresAt,
        organizationId: data.organizationId,
        organizationName: data.organizationName,
        invitedEmail: data.invitedEmail,
        role: data.role,
        inviterUserId: data.inviterUserId,
        metadata: data.metadata
      };

      // 署名生成
      const signaturePayload = JSON.stringify({
        token: tokenData.token,
        expiresAt: tokenData.expiresAt,
        organizationId: tokenData.organizationId,
        invitedEmail: tokenData.invitedEmail,
        role: tokenData.role
      });

      tokenData.signature = CryptoJS.HmacSHA256(signaturePayload, SECRET_KEY).toString();

      // Base64エンコード
      const encodedToken = btoa(JSON.stringify(tokenData));

      logger.info('[InvitationService] トークン生成成功:', {
        email: data.invitedEmail,
        role: data.role,
        expiresAt: new Date(expiresAt).toISOString()
      });

      return encodedToken;
    } catch (error) {
      logger.error('[InvitationService] トークン生成エラー:', error);
      throw new Error('招待トークンの生成に失敗しました');
    }
  }

  /**
   * トークン検証
   */
  static validateToken(encodedToken: string): TokenValidationResult {
    try {
      this.initializeCache();

      // Base64デコード
      const decoded = atob(encodedToken);
      const tokenData: SecureInvitationToken = JSON.parse(decoded);

      // 使用済みチェック
      if (this.isTokenUsed(tokenData.token)) {
        logger.warn('[InvitationService] 使用済みトークン:', tokenData.token);
        return { valid: false, used: true, error: '既に使用されたトークンです' };
      }

      // 有効期限チェック
      if (Date.now() > tokenData.expiresAt) {
        logger.warn('[InvitationService] 期限切れトークン:', {
          token: tokenData.token,
          expiredAt: new Date(tokenData.expiresAt).toISOString()
        });
        return { valid: false, expired: true, error: 'トークンの有効期限が切れています' };
      }

      // 署名検証
      const signaturePayload = JSON.stringify({
        token: tokenData.token,
        expiresAt: tokenData.expiresAt,
        organizationId: tokenData.organizationId,
        invitedEmail: tokenData.invitedEmail,
        role: tokenData.role
      });

      const expectedSignature = CryptoJS.HmacSHA256(signaturePayload, SECRET_KEY).toString();

      if (tokenData.signature !== expectedSignature) {
        logger.error('[InvitationService] 署名不一致:', {
          token: tokenData.token,
          expected: expectedSignature,
          actual: tokenData.signature
        });
        return { valid: false, tampered: true, error: 'トークンが改ざんされている可能性があります' };
      }

      logger.info('[InvitationService] トークン検証成功:', {
        token: tokenData.token,
        email: tokenData.invitedEmail
      });

      return { valid: true, data: tokenData };
    } catch (error) {
      logger.error('[InvitationService] トークン検証エラー:', error);
      return { valid: false, error: 'トークンの検証に失敗しました' };
    }
  }

  /**
   * トークン使用済みチェック
   */
  static isTokenUsed(token: string): boolean {
    this.initializeCache();
    return this.usedTokensCache.has(token);
  }

  /**
   * トークンを使用済みとしてマーク
   */
  static async markAsUsed(token: string, userId?: string): Promise<void> {
    try {
      this.initializeCache();

      // メモリキャッシュに追加
      this.usedTokensCache.add(token);

      // LocalStorageに永続化
      const storedTokens = localStorage.getItem(USED_TOKENS_KEY) || '{}';
      const tokens = JSON.parse(storedTokens);

      tokens[token] = {
        usedAt: Date.now(),
        usedBy: userId,
        timestamp: new Date().toISOString()
      };

      // 古いトークンをクリーンアップ（90日以上前のものを削除）
      const cutoffTime = Date.now() - (90 * 24 * 60 * 60 * 1000);
      Object.keys(tokens).forEach(key => {
        if (tokens[key].usedAt < cutoffTime) {
          delete tokens[key];
          this.usedTokensCache.delete(key);
        }
      });

      localStorage.setItem(USED_TOKENS_KEY, JSON.stringify(tokens));

      // サーバー通信のシミュレーション（本番ではAPIコール）
      await this.simulateServerUpdate(token, userId);

      logger.info('[InvitationService] トークン使用済みマーク完了:', {
        token,
        userId
      });
    } catch (error) {
      logger.error('[InvitationService] トークン使用済みマークエラー:', error);
      throw new Error('トークンの使用済み記録に失敗しました');
    }
  }

  /**
   * サーバー更新のシミュレーション
   */
  private static async simulateServerUpdate(token: string, userId?: string): Promise<void> {
    // 本番環境ではここでAPIを呼び出す
    if (import.meta.env.PROD) {
      // await ApiClient.post('/api/invitations/mark-used', { token, userId });
      logger.info('[InvitationService] サーバー更新をシミュレート:', { token, userId });
    }

    // IndexedDBへの保存（より永続的な保存）
    if ('indexedDB' in window) {
      try {
        await this.saveToIndexedDB(token, userId);
      } catch (error) {
        logger.warn('[InvitationService] IndexedDB保存失敗:', error);
      }
    }
  }

  /**
   * IndexedDBへの保存
   */
  private static async saveToIndexedDB(token: string, userId?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('SalonHubSecurity', 1);

      request.onerror = () => reject(request.error);

      request.onsuccess = () => {
        const db = request.result;
        const transaction = db.transaction(['usedTokens'], 'readwrite');
        const store = transaction.objectStore('usedTokens');

        store.put({
          token,
          usedAt: Date.now(),
          usedBy: userId,
          timestamp: new Date().toISOString()
        });

        transaction.oncomplete = () => {
          db.close();
          resolve();
        };

        transaction.onerror = () => {
          db.close();
          reject(transaction.error);
        };
      };

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains('usedTokens')) {
          const store = db.createObjectStore('usedTokens', { keyPath: 'token' });
          store.createIndex('usedAt', 'usedAt', { unique: false });
        }
      };
    });
  }

  /**
   * ランダムトークン生成
   */
  private static generateRandomToken(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * 使用済みトークンのクリーンアップ
   */
  static async cleanupExpiredTokens(): Promise<void> {
    try {
      this.initializeCache();

      const storedTokens = localStorage.getItem(USED_TOKENS_KEY) || '{}';
      const tokens = JSON.parse(storedTokens);

      // 90日以上前のトークンを削除
      const cutoffTime = Date.now() - (90 * 24 * 60 * 60 * 1000);
      let cleanedCount = 0;

      Object.keys(tokens).forEach(key => {
        if (tokens[key].usedAt < cutoffTime) {
          delete tokens[key];
          this.usedTokensCache.delete(key);
          cleanedCount++;
        }
      });

      if (cleanedCount > 0) {
        localStorage.setItem(USED_TOKENS_KEY, JSON.stringify(tokens));
        logger.info(`[InvitationService] ${cleanedCount}個の期限切れトークンをクリーンアップ`);
      }
    } catch (error) {
      logger.error('[InvitationService] クリーンアップエラー:', error);
    }
  }
}

// 定期的なクリーンアップ（1日1回）
if (typeof window !== 'undefined') {
  setInterval(() => {
    InvitationService.cleanupExpiredTokens();
  }, 24 * 60 * 60 * 1000);
}

export default InvitationService;